import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../FirebaseAuth";
import { userSignOut } from "../../../libs/user";
import { confirmAlert } from "react-confirm-alert";
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import {
  AccountCircle as AccountBoxIcon,
  ListAlt as ListAltIcon,
  Logout as ExitToAppIcon,
  Business as ApartmentIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import { Firestore } from "../../FirebaseAuth/firebase";
import { doc, getDoc } from "firebase/firestore";

const UserMenu = () => {
  const theme = useTheme();
  const initialUserState = {
    isLoading: true,
    data: null,
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { authUser } = useContext(AuthContext);
  const history = useNavigate();

  const [userData, setUserData] = useState(initialUserState);
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const getUserData = useCallback(async () => {
    try {
      const userDocRef = doc(Firestore, "users", authUser.user.uid);
      const refUser = await getDoc(userDocRef);
      const userData = await refUser.data();
      setUserData({
        data: userData,
        isLoading: false,
      });
    } catch (ex) {
      console.log("Error fetching user data:", ex);
    }
  }, [authUser]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path) => {
    handleClose();
    history(path);
  };

  useEffect(() => {
    getUserData();
  }, [getUserData]);

  return (
    <Box>
      <IconButton
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        sx={{
          p: 0.5,
          border: "2px solid rgba(255, 255, 255, 0.2)",
          borderRadius: 2,
        }}
      >
        <Avatar
          alt={authUser.user.displayName}
          src={authUser.user.photoURL}
          sx={{ width: 36, height: 36 }}
        />
      </IconButton>

      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 220,
            mt: 1.5,
            borderRadius: 2,
            border: "1px solid rgba(0, 0, 0, 0.05)",
            py: 1,
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1.5, mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {authUser.user.displayName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ wordBreak: "break-all" }}
          >
            {authUser.user.email}
          </Typography>
        </Box>
        <Divider />

        <MenuItem
          onClick={() => handleNavigate("/user/profile")}
          sx={{
            py: 1.5,
            mx: 1,
            my: 0.5,
            borderRadius: 1,
          }}
        >
          <ListItemIcon>
            <AccountBoxIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </MenuItem>

        <MenuItem
          onClick={() => handleNavigate("/user/organization")}
          sx={{
            py: 1.5,
            mx: 1,
            my: 0.5,
            borderRadius: 1,
          }}
        >
          <ListItemIcon>
            <ApartmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Organization" />
        </MenuItem>

        <MenuItem
          onClick={() => handleNavigate("/user/log")}
          sx={{
            py: 1.5,
            mx: 1,
            my: 0.5,
            borderRadius: 1,
          }}
        >
          <ListItemIcon>
            <ListAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Activity Logs" />
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={() => {
            window.open("https://help.kwallcompany.com/", "_blank");
            handleClose();
          }}
          sx={{
            py: 1.5,
            mx: 1,
            my: 0.5,
            borderRadius: 1,
          }}
        >
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Help & Support" />
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={() => {
            handleClose();
            confirmAlert({
              title: "Sign Out",
              message: "Are you sure you want to sign out?",
              buttons: [
                {
                  label: "Yes",
                  onClick: () => {
                    userSignOut();
                    history("/welcome");
                  },
                  className: "react-confirm-alert-button-delete",
                },
                {
                  label: "No",
                  className: "react-confirm-alert-button-cancel",
                  onClick: () => {},
                },
              ],
            });
          }}
          sx={{
            py: 1.5,
            mx: 1,
            my: 0.5,
            borderRadius: 1,
            color: theme.palette.error.main,
          }}
        >
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{
              color: "inherit",
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
